// ================ Action types ================ //

import { notifyMailContactEvent } from '../../ducks/mailContact.duck';
import { currentUserShowSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { updateOneSignalTags } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { logError } from '../../util/log';
import { isCordova } from '../../util/mobile/deviceDetection';
import { oneSignalAddTags, oneSignalSetLanguage } from '../../util/mobile/oneSignal';

// ================ Action types ================ //

export const UPDATE_NOTIFICATION_SETTINGS_REQUEST =
  'app/NotificationSettingsPage/UPDATE_NOTIFICATION_SETTINGS_REQUEST';
export const UPDATE_NOTIFICATION_SETTINGS_SUCCESS =
  'app/NotificationSettingsPage/UPDATE_NOTIFICATION_SETTINGS_SUCCESS';
export const UPDATE_NOTIFICATION_SETTINGS_ERROR =
  'app/NotificationSettingsPage/UPDATE_NOTIFICATION_SETTINGS_ERROR';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.NotificationSettingsPage} */
const initialState = {
  updateNotificationSettingsError: null,
  updateNotificationSettingsInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPDATE_NOTIFICATION_SETTINGS_REQUEST:
      return {
        ...state,
        updateNotificationSettingsInProgress: true,
        updateNotificationSettingsError: null,
      };
    case UPDATE_NOTIFICATION_SETTINGS_SUCCESS:
      return { ...state, updateNotificationSettingsInProgress: false };
    case UPDATE_NOTIFICATION_SETTINGS_ERROR:
      return {
        ...state,
        updateNotificationSettingsInProgress: false,
        updateNotificationSettingsError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const updateNotificationSettingsRequest = () => ({
  type: UPDATE_NOTIFICATION_SETTINGS_REQUEST,
});
export const updateNotificationSettingsSuccess = () => ({
  type: UPDATE_NOTIFICATION_SETTINGS_SUCCESS,
});
export const updateNotificationSettingsError = (error) => ({
  type: UPDATE_NOTIFICATION_SETTINGS_ERROR,
  payload: error,
});

// ================ Thunks ================ //

/**
 * @param {object} params
 * @param {VanSite.Notification.Settings} params.notificationSettings
 * @param {string} params.notificationLocale - locale for the notification
 * @returns {(dispatch: any, getState: any, sdk: any) => Promise<VanSite.CurrentUser>} updated current user
 */

export const updateNotificationSettings = (params) => async (dispatch, getState, sdk) => {
  dispatch(updateNotificationSettingsRequest());
  try {
    const response = await sdk.currentUser.updateProfile(
      {
        privateData: { ...params },
      },
      { expand: true },
    );

    /** @type {VCurrentUser} */
    const currentUser = getState().user.currentUser;
    if (isCordova()) {
      oneSignalAddTags({ receive_news: params.notificationSettings.push.news });
      oneSignalSetLanguage(params.notificationLocale);
    } else {
      await updateOneSignalTags({
        externalUserId: currentUser.id.uuid,
        tags: {
          receive_news: params.notificationSettings.push.news,
        },
      });
    }

    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.show response');
    }
    const updatedCurrentUser = entities[0];
    dispatch(currentUserShowSuccess(updatedCurrentUser));

    // Nach dem Store-Update, aber unabhängig davon: der Server liest den
    // frischen Flex-Zustand (Newsletter-Flag, notificationLocale) direkt über
    // das Trusted SDK, nicht mehr den Store - Sprachwechsel und Newsletter
    // gehen deshalb in einem einzigen Ereignis raus.
    dispatch(notifyMailContactEvent('notification-settings-updated'));

    dispatch(updateNotificationSettingsSuccess());
    return updatedCurrentUser;
  } catch (e) {
    logError(e, 'update-notification-settings-failed', { ...params });
    dispatch(updateNotificationSettingsError(storableError(e)));
  }
};

export const loadData = () => async (dispatch) => {
  return dispatch(fetchCurrentUser());
};
